import type { ExportOptions, OrderOptions } from '@bkbnlab/api'
import {
  DEFAULT_LAB_ENHANCE_SETTINGS,
  LAB_DEFAULT_PRIVACY_CATEGORIES,
  LAB_PRIVACY_CATEGORIES,
  LAB_STAGE_MODES_WITH_SPEED,
  LabCombinedSettings,
  LabEnhanceMode,
  LabEnhanceSettings,
  LabEraseSettings,
  LabOrderOptions,
  LabLocalStage,
  LabPrivacyCategory,
  LAB_CUSTOM_STYLE,
  LabRoomType,
  LabStageLook,
  LabStageSettings
} from './labSdk.types'
import { LAB_DESCRIBED_LOOKS, LAB_DESCRIBED_LOOK_BASE, LAB_LOOK_ROOM_TYPE, isLabDescribedLook } from './labStageLooks'

/**
 * Basic mode ignores the advanced toggles and runs the recommended set; only Replace sky is the user's.
 * Advanced mode sends exactly what is toggled on.
 */
export const resolveLabEnhanceSettings = (settings: LabEnhanceSettings): LabEnhanceSettings =>
  settings.mode === LabEnhanceMode.BASIC
    ? { ...DEFAULT_LAB_ENHANCE_SETTINGS, replaceSky: settings.replaceSky, skyStyle: settings.skyStyle }
    : settings

const hasAllPrivacyCategories = (categories: LabEnhanceSettings['privacyCategories']) =>
  LAB_PRIVACY_CATEGORIES.every((category) => categories.includes(category))

/**
 * Enhance → `bkbn.order(...)`. `undefined` means "not requested" and is dropped by the SDK — never send
 * `false`/`null` placeholders, the server default fills the gap.
 */
export const buildLabEnhanceOrderOptions = (inputAssetId: string, settings: LabEnhanceSettings): OrderOptions => {
  const s = resolveLabEnhanceSettings(settings)
  const options: OrderOptions = { inputs: [inputAssetId] }

  if (s.perspective) {
    options.perspective = s.perspectiveStrength >= 1 ? true : { strength: s.perspectiveStrength }
  }
  if (s.tonecraft) {
    options.tonecraft = { profile: s.tonecraftProfile, levels: s.autoLevels }
  }
  if (s.privacy && s.privacyCategories.length > 0) {
    options.privacy = hasAllPrivacyCategories(s.privacyCategories) ? true : { categories: [...s.privacyCategories] }
  }
  if (s.replaceSky) {
    options.sky = s.skyStyle
  }
  if (s.imageFormat) {
    options.format = s.format
    if (s.format !== 'png') {
      options.quality = s.quality
    }
  }
  return options
}

/**
 * An Enhance order needs at least one operation — Advanced with everything off would spend an order and a full wait
 * to hand the input back — so the panel disables Generate on the same rule. Basic always runs the recommended set.
 */
export const isLabEnhanceSettingsValid = (settings: LabEnhanceSettings): boolean => {
  const s = resolveLabEnhanceSettings(settings)
  return s.perspective || s.tonecraft || (s.privacy && s.privacyCategories.length > 0) || s.replaceSky || s.imageFormat
}

/**
 * Auto privacy and its categories move together, so the panel never shows privacy on while the order drops it:
 * unchecking the last category switches it off, checking one switches it back on.
 */
export const toggleLabPrivacyCategory = (settings: LabEnhanceSettings, category: LabPrivacyCategory, checked: boolean): Partial<LabEnhanceSettings> => {
  const others = settings.privacyCategories.filter((item) => item !== category)
  if (checked) return { ...(settings.privacy ? {} : { privacy: true }), privacyCategories: [...others, category] }
  return others.length > 0 ? { privacyCategories: others } : { privacy: false, privacyCategories: others }
}

/** Switching Auto privacy on with no category left selects the default ones again (photos & frames stays opt-in). */
export const toggleLabPrivacy = (settings: LabEnhanceSettings, on: boolean): Partial<LabEnhanceSettings> =>
  on && settings.privacyCategories.length === 0 ? { privacy: true, privacyCategories: [...LAB_DEFAULT_PRIVACY_CATEGORIES] } : { privacy: on }

/**
 * "Keep as is" with both surfaces kept has nothing to run — the Lab API refuses the order, so the panel
 * disables Generate on the same rule.
 */
export const isLabStageSettingsValid = (settings: LabStageSettings): boolean =>
  !(settings.mode === 'indoor_staging' && settings.style === 'keep' && settings.floorFinish === 'keep' && settings.wallColor === 'keep')

/**
 * A Quality tier's run time as its segment shows it. The estimates are rough, so the display stays round: 15 s steps
 * up to a minute, half minutes above (27 → ~30 s, 44 → ~45 s, 60 → ~60 s, 90 → ~1.5 min).
 */
export const roundLabSpeedEstimate = (seconds: number): { unit: 'seconds' | 'minutes', value: number } => {
  const roundedSeconds = Math.max(15, Math.round(seconds / 15) * 15)
  return roundedSeconds <= 60 ? { unit: 'seconds', value: roundedSeconds } : { unit: 'minutes', value: Math.round(seconds / 30) / 2 }
}

/** Stage → `bkbn.order(...)`. Room-only options ride along for "Stage a room"; Speed only where the catalog honours it. */
export const buildLabStageOrderOptions = (inputAssetId: string, settings: LabStageSettings): LabOrderOptions => {
  const options: LabOrderOptions = { inputs: [inputAssetId], genEdit: settings.mode }

  if (settings.mode === 'indoor_staging') {
    const look = settings.style
    // Photo Studio's own looks run on a Lab look, their text sent as the described style
    options.stagingStyle = isLabDescribedLook(look) ? LAB_DESCRIBED_LOOK_BASE : look
    // A typed description wins; blank = the look's own text (the Lab's, or ours). Lab refuses a description with 'keep'
    const described = settings.customStyle && look !== 'keep' ? settings.styleClause.trim() : ''
    const styleClause = described || (isLabDescribedLook(look) ? LAB_DESCRIBED_LOOKS[look] : '')
    if (styleClause) options.styleClause = styleClause
    const roomType = resolveLabRoomType(settings)
    if (roomType !== 'auto') options.roomType = roomType
    if (settings.floorFinish !== 'keep') options.floorFinish = settings.floorFinish
    if (settings.wallColor !== 'keep') options.wallColor = settings.wallColor
    // Only where the Ceiling light row shows: never a choice the user could not see
    if (settings.ceilingLight === 'style' && hasLabCeilingLight(settings)) options.ceilingLight = 'style'
  }
  if (LAB_STAGE_MODES_WITH_SPEED.includes(settings.mode)) {
    options.genQuality = settings.speed
  }
  return options
}

/** The room type a run stages as: the look's own (business spaces → Office), else the Type select's */
export const resolveLabRoomType = (settings: LabStageSettings): LabRoomType =>
  (isLabDescribedLook(settings.style) ? LAB_LOOK_ROOM_TYPE[settings.style] : undefined) ?? settings.roomType

/** The look decides the room type, so the Type select shows it, disabled */
export const isLabRoomTypeLocked = (settings: LabStageSettings): boolean =>
  isLabDescribedLook(settings.style) && LAB_LOOK_ROOM_TYPE[settings.style] !== undefined

/**
 * The Ceiling light row applies — shown, and its choice sent — for a room staged in a home look. "Keep as is" has no
 * look to restage the light in (Lab refuses it there), and a business space (the looks on their own room type) keeps
 * the light as photographed.
 */
export const hasLabCeilingLight = (settings: LabStageSettings): boolean =>
  settings.mode === 'indoor_staging' && settings.style !== 'keep' && !isLabRoomTypeLocked(settings)

/**
 * A Room style tile picked — the Lab Playground's rule: "Describe" (`LAB_CUSTOM_STYLE`) is a mode switched on
 * and off from its tile, keeping what was typed, and starting from "Keep as is" furnishes in Contemporary (Lab refuses
 * a description with 'keep'); picking a look turns the mode off.
 */
export const selectLabStageStyle = (settings: LabStageSettings, tileId: string): Partial<LabStageSettings> => {
  if (tileId !== LAB_CUSTOM_STYLE) return { customStyle: false, style: tileId as LabStageLook }
  if (settings.customStyle) return { customStyle: false }
  return settings.style === 'keep' ? { customStyle: true, style: 'contemporary' } : { customStyle: true }
}

/** Erase → `bkbn.order(...)`: exactly one input and a brush mask asset (white on transparent PNG). */
export const buildLabEraseOrderOptions = (inputAssetId: string, maskAssetId: string): OrderOptions => ({
  inputs: [inputAssetId],
  genEdit: 'magic_eraser',
  mask: maskAssetId,
})

/**
 * Expected Lab order stages, for the progress bar under the overlay text. Mirrors the API's stage
 * builder (edit → [sky] → [detect] → finish for enhance; edit → [room] → gen/fal → finish for staging;
 * gen → finish for the eraser). The SDK reports the live stage; this only decides which segments to draw.
 */
export const buildLabStagePlan = (kind: 'enhance' | 'stage' | 'erase', settings?: LabEnhanceSettings | LabStageSettings | LabEraseSettings): string[] => {
  if (kind === 'erase') return ['gen', 'finish']
  if (kind === 'stage') {
    const s = settings as LabStageSettings | undefined
    if (s?.mode === 'declutter_local') return ['edit', 'detect', 'gen', 'finish']
    const room = s?.mode === 'indoor_staging' && s.style !== 'keep' ? ['room'] : []
    return ['edit', ...room, 'fal', 'finish']
  }
  const s = settings ? resolveLabEnhanceSettings(settings as LabEnhanceSettings) : undefined
  return ['edit', ...(s?.replaceSky ? ['sky'] : []), ...(s?.privacy ? ['detect'] : []), 'finish']
}

/**
 * All features at once → one `bkbn.order(...)`: the Enhance operations merged with the run's generative edit (Stage's
 * genEdit, or `magic_eraser` + mask). With a single feature on, the order is exactly that tool's own order.
 */
export const buildLabCombinedOrderOptions = (inputAssetId: string, { enhance, generative }: LabCombinedSettings, maskAssetId?: string): LabOrderOptions => {
  const generativeOptions = generative?.kind === 'erase'
    ? buildLabEraseOrderOptions(inputAssetId, maskAssetId ?? '')
    : generative?.kind === 'stage'
      ? buildLabStageOrderOptions(inputAssetId, generative.settings)
      : { inputs: [inputAssetId] }
  return enhance ? { ...buildLabEnhanceOrderOptions(inputAssetId, enhance), ...generativeOptions } : generativeOptions
}

/**
 * Stage plan of a combined run: the Enhance develop (and sky) first, the generative edit, then the privacy detection —
 * each step once. A single feature keeps its own tool's plan.
 */
export const buildLabCombinedPlan = ({ enhance, generative }: LabCombinedSettings): string[] => {
  if (!enhance) {
    if (generative?.kind === 'stage') return buildLabStagePlan('stage', generative.settings)
    return generative ? buildLabStagePlan('erase') : []
  }
  if (!generative) return buildLabStagePlan('enhance', enhance)
  const s = resolveLabEnhanceSettings(enhance)
  const generativeSteps = generative.kind === 'stage'
    ? buildLabStagePlan('stage', generative.settings).filter((step) => step !== 'edit' && step !== 'finish')
    : ['gen']
  const steps = ['edit', ...(s.replaceSky ? ['sky'] : []), ...generativeSteps, ...(s.privacy ? ['detect'] : []), 'finish']
  return steps.filter((step, index) => steps.indexOf(step) === index)
}

/** The order's Develop stage (`edit`): Enhance and Stage orders start with it */
const LAB_DEVELOP_STAGE = 'edit'

/**
 * The overlay's plan: the client-side "Prepare" step (uploads, order submission, queue — see LabLocalStage) in front of
 * the order's own stages, so the wait before the order runs never reads as its first stage at 0%. An order that starts
 * with Develop shows no Prepare segment — the two read as the same step (2026-10-08): the client-side steps run inside
 * Develop instead, still named (useLabProgressDisplay). The eraser's plan (Generate, Finish) keeps Prepare.
 */
export const withLabPrepareStep = (plan: string[]): string[] => plan[0] === LAB_DEVELOP_STAGE ? plan : [LabLocalStage.PREPARE, ...plan]

/** Keywords the Lab feed uses in `progressStage` / sub-labels, mapped onto the plan's stage keys. */
const LAB_STAGE_KEYWORDS: Array<[RegExp, string]> = [
  [/^(prepare|upload|queued|ingest)/, 'upload'],
  [/^(edit|develop|decode|hdr|cap6k|perspective|passthrough)/, 'edit'],
  [/^(sky)/, 'sky'],
  [/^(fal|gpt|generat|sampl|gen|remove|declutter|inpaint|denois)/, 'gen'],
  [/^(detect|sam3|sam$|mask|segment|window)/, 'detect'],
  [/^(room)/, 'room'],
  [/^(finish|composit|guard|tonecraft|blur|sharpen|levels)/, 'finish'],
  [/^(package|encode|export|done)/, 'package'],
]

/**
 * Maps a live Lab stage label onto the plan (`buildLabStagePlan`). The feed sends coarse stage names
 * (`edit`, `gen`, `fal-1`) but also sub-labels (`sampling 5/8`), so match by keyword; `gen`/`fal` are
 * interchangeable across plans; unknown labels fall back to the segment the overall percentage
 * suggests rather than to the first stage.
 */
export const resolveLabStageIndex = (stage: string, plan: string[], percent: number): number => {
  if (plan.length === 0) return 0
  const lower = stage.trim().toLowerCase()
  const keyword = LAB_STAGE_KEYWORDS.find(([pattern]) => pattern.test(lower))?.[1] ?? (plan.includes(lower) ? lower : undefined)
  if (keyword === 'upload') return 0
  if (keyword === 'package') return plan.length - 1
  if (keyword) {
    const candidates = keyword === 'gen' ? ['gen', 'fal'] : [keyword]
    const index = plan.findIndex((item) => candidates.includes(item))
    if (index >= 0) return index
  }
  // The order's percentage spreads over its own stages, never back onto the client-side Prepare step
  const offset = plan[0] === LabLocalStage.PREPARE ? 1 : 0
  const orderStages = plan.length - offset
  return Math.min(plan.length - 1, offset + Math.max(0, Math.floor((percent / 100) * orderStages)))
}

/**
 * The rendition downloaded through the SDK and kept as a local blob for preview and gallery save.
 */
export const resolveLabExportOptions = (settings?: LabEnhanceSettings): ExportOptions => {
  const s = settings ? resolveLabEnhanceSettings(settings) : undefined
  const format = s?.imageFormat ? s.format : DEFAULT_LAB_ENHANCE_SETTINGS.format
  const quality = s?.imageFormat ? s.quality : DEFAULT_LAB_ENHANCE_SETTINGS.quality
  return format === 'png' ? { format } : { format, quality }
}
